import React from 'react';

import { Typography } from '@mui/material';
import { getAddressFieldsConfig } from 'app/pages/loanApplication/LoanApplication.sharedConfigs';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import dayjs from 'dayjs';
import { compose } from 'redux';

import { COMMON_DATE_FORMAT, MIN_CALENDAR_DATE, TODAY_IN_DAYS } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { BaseStatus, HousingType } from 'constants/loanApplication';
import { baseStatusOptions, housingTypeOptions } from 'constants/options';
import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';
import { formatNumberToCurrencyString } from 'utils/number';

import { BorrowersTestIds } from '../../../../../Borrowers.testIds';

import ActionButton from './ActionButton';
import { PreviousAddressTableColumnId } from './Address.constants';
import { emptyMessageCellStyles, typographyStyles } from './Address.styles';

export const getAddressConfigFields = ({ entityId, isPreviousResidence }) => {
  const entityPrefix = isPreviousResidence ? '' : `[${entityId}].`;
  const currentResidencePrefix = isPreviousResidence ? '' : `${entityPrefix}currentResidence.`;

  return {
    housingType: {
      fieldId: 'housingType',
      type: FieldType.SELECT,
      nameInModel: `${currentResidencePrefix}housingType`,
      inputProps: {
        label: 'Ownership Type',
        required: true,
        disabled: false,
        options: housingTypeOptions,
        variant: 'outlined',
        placeholder: 'Ownership Type',
        dataTestId: BorrowersTestIds.ADDRESS_FORM_HOUSING_TYPE_SELECT,
        'data-test-value': housingTypeOptions[1].name,
      },
    },
    monthlyRentAmount: {
      fieldId: 'monthlyRentAmount',
      type: FieldType.INPUT,
      nameInModel: `${currentResidencePrefix}monthlyRentAmount`,
      inputProps: {
        placeholder: 'Type Monthly Rent',
        label: 'Monthly Rent',
        required: true,
        disabled: false,
        variant: 'outlined',
        type: FieldInputType.CURRENCY,
        dataTestId: BorrowersTestIds.ADDRESS_FORM_MONTHLY_RENT_AMOUNT_INPUT,
        'data-test-value': '200000',
        'data-test-formatted-value': '$200,000',
      },
    },
    hasRelativesPartRent: {
      fieldId: 'hasRelativesPartRent',
      type: FieldType.EXTENDABLE_RADIO,
      nameInModel: `${currentResidencePrefix}hasRelativesPartRent`,
      inputProps: {
        label: 'Do you pay any rent?',
        disabled: false,
        required: true,
        options: baseStatusOptions,
        columns: 4,
        align: ItemGroupAlignment.HORIZONTAL,
        labelPlacement: ItemLabelPlacement.START,
        className: 'flex-row w-full',
      },
    },
    monthlyRelativesPartRentAmount: {
      fieldId: 'monthlyRelativesPartRentAmount',
      type: FieldType.INPUT,
      nameInModel: `${currentResidencePrefix}monthlyRelativesPartRentAmount`,
      inputProps: {
        placeholder: 'Type Monthly Rent',
        label: 'Monthly Rent',
        variant: 'outlined',
        type: FieldInputType.CURRENCY,
        dataTestId: BorrowersTestIds.ADDRESS_FORM_MONTHLY_RELATIVES_PART_RENT_AMOUNT_INPUT,
        'data-test-value': '200000',
        'data-test-formatted-value': '$200,000.00',
        customStyles: { maxWidth: '320px' },
      },
    },
    startOfResidence: {
      fieldId: 'startOfResidence',
      type: FieldType.DATE,
      nameInModel: `${currentResidencePrefix}startOfResidence`,
      inputProps: {
        label: 'Start Of Residence',
        disabled: false,
        required: true,
        includeLocalOffset: true,
        variant: 'outlined',
        format: COMMON_DATE_FORMAT,
        dataTestId: BorrowersTestIds.ADDRESS_FORM_START_OF_RESIDENCE_INPUT,
        'data-test-value': dayjs().subtract(1, 'day'),
      },
    },
    endOfResidence: {
      fieldId: 'endOfResidence',
      type: FieldType.DATE,
      nameInModel: `${currentResidencePrefix}endOfResidence`,
      inputProps: {
        label: 'End Of Residence',
        disabled: false,
        required: true,
        includeLocalOffset: true,
        variant: 'outlined',
        maxDate: TODAY_IN_DAYS,
        format: COMMON_DATE_FORMAT,
        dataTestId: BorrowersTestIds.ADDRESS_FORM_END_OF_RESIDENCE_INPUT,
        'data-test-value': dayjs(),
      },
    },
    isMailingAddressTheSameAsCurrentAddress: {
      fieldId: 'isMailingAddressTheSameAsCurrentAddress',
      type: FieldType.CHECKBOX,
      nameInModel: `${entityPrefix}isMailingAddressTheSameAsCurrentAddress`,
      inputProps: {
        label: 'Mailing address is the same as current address',
        className: 'is-mailing-address-the-same-as-current-address',
        disabled: false,
        required: false,
        variant: 'outlined',
        customStyles: {
          marginBottom: '-15px !important',
        },
        columns: 4,
        'data-testid': BorrowersTestIds.ADDRESS_FORM_IS_MAILING_ADDRESS_THE_SAME_CHECKBOX,
        'data-test-value': false,
      },
    },
    isSubjectProperty: {
      fieldId: 'isSubjectProperty',
      type: FieldType.CHECKBOX,
      nameInModel: `${currentResidencePrefix}isSubjectProperty`,
      inputProps: {
        label: 'Subject Property',
        className: 'subject-property',
        required: false,
        disabled: false,
        columns: 4,
      },
    },
  };
};

const getOwnershipStatusConfigFields = ({ fields, isAllFieldsDisabled, ownershipType }) => [
  {
    ...fields.housingType,
    inputProps: {
      ...fields.housingType.inputProps,
      disabled: isAllFieldsDisabled,
    },
  },
  ...(ownershipType === HousingType.RENT
    ? [
        {
          ...fields.monthlyRentAmount,
          inputProps: {
            ...fields.monthlyRentAmount.inputProps,
            disabled: isAllFieldsDisabled,
          },
        },
      ]
    : []),
];

export const getAddressConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    entityId,
    section,
    addressCounties,
    mailingAddressCounties,
    ownershipType,
    hasRelativesPartRent,
    isSubjectPropertyCheckboxVisible,
    isLiveTogetherForTwoYears,
    isSubjectProperty,
    addressIsOutsideUsa,
    nonUSCountries,
    isMilitaryAddressAllowed,
    isMailingAddressTheSameAsCurrentAddress,
    mailingAddressIsOutsideUsa,
    onChangeIsOutsideUsa,
    onChangeIsSubjectProperty,
    isPreviousResidence = false,
    endOfResidence,
    startOfResidence,
  }) => {
    const fields = getAddressConfigFields({ entityId, isPreviousResidence });

    const isAllFieldsDisabled = isSubjectPropertyCheckboxVisible
      ? isSubjectProperty || isLiveTogetherForTwoYears
      : isLiveTogetherForTwoYears;
    const isStartDateDisabled = isSubjectPropertyCheckboxVisible
      ? isSubjectProperty && isLiveTogetherForTwoYears
      : isLiveTogetherForTwoYears;
    const isEndDateDisabled = isSubjectPropertyCheckboxVisible
      ? isSubjectProperty && isLiveTogetherForTwoYears
      : isLiveTogetherForTwoYears;
    const isLivingWithRelative = ownershipType === HousingType.LIVING_WITH_RELATIVE;

    const ownershipStatusConfig = getOwnershipStatusConfigFields({
      fields,
      isAllFieldsDisabled,
      ownershipType,
      hasRelativesPartRent,
    });

    const checkboxGroupFields = [
      {
        ...fields.isMailingAddressTheSameAsCurrentAddress,
        inputProps: {
          ...fields.isMailingAddressTheSameAsCurrentAddress.inputProps,
          disabled: isAllFieldsDisabled,
        },
      },
      ...getConditionalArray(isSubjectPropertyCheckboxVisible && !isPreviousResidence, [
        {
          ...fields.isSubjectProperty,
          inputProps: {
            ...fields.isSubjectProperty.inputProps,
            disabled: isLiveTogetherForTwoYears,
            customOnChange: onChangeIsSubjectProperty,
          },
        },
      ]),
      ...getConditionalArray(!isMailingAddressTheSameAsCurrentAddress && !isPreviousResidence, () =>
        Object.values(
          getAddressFieldsConfig({
            prefix: `[${entityId}].mailingAddress`,
            isAllFieldsDisabled: isLiveTogetherForTwoYears,
            isOutsideUsa: mailingAddressIsOutsideUsa,
            nonUSCountries,
            isStatesWithMilitaryAddresses: isMilitaryAddressAllowed,
            filteredCounties: mailingAddressCounties,
            onChangeIsOutsideUsa,
          }),
        ),
      ),
    ];

    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: Object.values(
            getAddressFieldsConfig({
              isAllFieldsDisabled,
              isOutsideUsa: addressIsOutsideUsa,
              nonUSCountries,
              prefix: isPreviousResidence ? '' : `[${entityId}].currentResidence`,
              isStatesWithMilitaryAddresses: isMilitaryAddressAllowed,
              filteredCounties: addressCounties,
              onChangeIsOutsideUsa,
            }),
          ),
          key: `${entityId}-${section}-address`,
          lockAsGroup: true,
        },
        {
          type: FieldType.GROUP,
          fields: [
            {
              ...fields.startOfResidence,
              inputProps: {
                ...fields.startOfResidence.inputProps,
                disabled: isStartDateDisabled,
                maxDate: dayjs(endOfResidence || TODAY_IN_DAYS).add(-1, 'day'),
              },
            },
            ...getConditionalArray(isPreviousResidence, () => [
              {
                ...fields.endOfResidence,
                inputProps: {
                  ...fields.endOfResidence.inputProps,
                  disabled: isEndDateDisabled,
                  minDate: dayjs(startOfResidence || MIN_CALENDAR_DATE).add(1, 'day'),
                },
              },
            ]),
            ...ownershipStatusConfig,
          ],
          key: `${entityId}-${section}-residence-info`,
        },
        ...getConditionalArray(isLivingWithRelative, [
          {
            type: FieldType.GROUP,
            customStyles: { gridTemplateColumns: `repeat(4, 1fr)` },
            fields: [
              {
                ...fields.hasRelativesPartRent,
                inputProps: {
                  ...fields.hasRelativesPartRent.inputProps,
                  disabled: isAllFieldsDisabled,
                },
                additionalContentBySelectedOption: {
                  [BaseStatus.YES]: {
                    groups: [
                      {
                        type: FieldType.GROUP,
                        fieldsClassName: 'flex justify-end',
                        fields: [
                          {
                            ...fields.monthlyRelativesPartRentAmount,
                            inputProps: {
                              ...fields.monthlyRelativesPartRentAmount.inputProps,
                              disabled: isAllFieldsDisabled,
                              required: hasRelativesPartRent,
                            },
                          },
                        ],
                      },
                    ],
                  },
                },
              },
            ],
            key: `${entityId}-${section}-living-with-relative-info`,
          },
        ]),
        ...getConditionalArray(!isPreviousResidence, [
          {
            type: FieldType.GROUP,
            fields: checkboxGroupFields,
            key: `${entityId}-${section}-checkbox-group`,
          },
        ]),
      ],
    };
  },
);

export const getPreviousAddressesTableConfig = ({
  setEditablePreviousAddressData,
  deletePreviousAddress,
  editableRowId,
  isPreviousAddressActionDisabled,
}) => ({
  [PreviousAddressTableColumnId.FULL_ADDRESS]: {
    label: 'Address',
    width: '33%',
    sort: true,
    fieldName: ['city', 'country', 'county', 'state', 'street', 'unit', 'zip'],
    colSpan: ({ fullAddress }) => fullAddress?.colSpan,
    customStyles: ({ fullAddress }) => fullAddress?.customStyles,
    content: ({ fullAddress }) =>
      fullAddress?.content ?? (
        <Typography data-truncated sx={typographyStyles} variant="regular14">
          {fullAddress}
        </Typography>
      ),
  },
  [PreviousAddressTableColumnId.START_OF_RESIDENCE]: {
    label: 'Start of Residence',
    sort: true,
    width: '14%',
    content: ({ startOfResidence }) => (
      <Typography data-truncated sx={typographyStyles} variant="regular14">
        {startOfResidence && dayjs(startOfResidence).format(COMMON_DATE_FORMAT)}
      </Typography>
    ),
  },
  [PreviousAddressTableColumnId.END_OF_RESIDENCE]: {
    label: 'End of Residence',
    sort: true,
    width: '14%',
    content: ({ endOfResidence }) => (
      <Typography data-truncated sx={typographyStyles} variant="regular14">
        {endOfResidence && dayjs(endOfResidence).format(COMMON_DATE_FORMAT)}
      </Typography>
    ),
  },
  [PreviousAddressTableColumnId.HOUSING_TYPE]: {
    label: 'Ownership Type',
    sort: true,
    width: '14%',
    content: ({ housingType }) => (
      <Typography data-truncated sx={typographyStyles} variant="regular14">
        {housingTypeOptions.find(option => option.value === housingType)?.name}
      </Typography>
    ),
  },
  [PreviousAddressTableColumnId.MONTHLY_RENT]: {
    label: 'Monthly Rent',
    sort: true,
    fieldName: ['monthlyRentAmount', 'monthlyRelativesPartRentAmount'],
    width: '14%',
    content: ({ monthlyRentAmount, monthlyRelativesPartRentAmount }) => (
      <Typography data-truncated sx={typographyStyles} variant="regular14">
        {monthlyRentAmount || monthlyRelativesPartRentAmount
          ? formatNumberToCurrencyString(monthlyRentAmount || monthlyRelativesPartRentAmount)
          : '-'}
      </Typography>
    ),
  },
  action: {
    label: 'Action',
    width: '11%',
    content: rowData => (
      <ActionButton
        deletePreviousAddress={deletePreviousAddress}
        isDisabled={
          isPreviousAddressActionDisabled || (!!editableRowId && rowData.id !== editableRowId)
        }
        isEditDisabled={rowData.id === editableRowId}
        rowData={rowData}
        setEditablePreviousAddressData={setEditablePreviousAddressData}
      />
    ),
    align: 'center',
  },
});

export const notFoundTableData = [
  {
    [PreviousAddressTableColumnId.FULL_ADDRESS]: {
      position: 1,
      customStyles: emptyMessageCellStyles,
      colSpan: 6,
      content: <NoAvailableMessage className="empty-message-row" message="No results" />,
    },
  },
];
